import { useCallback } from 'react';

import { PromoEvent } from 'common/enums/Tracking';
import type {
  CampaignTrackingData,
  PromoClickTrackingData,
} from 'common/tracking/campaignEventData';
import { getPromotionEventData } from 'common/tracking/campaignEventData';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';

interface UsePromoEventsParams extends CampaignTrackingData {
  mediaElement?: string;
}

export const usePromoEvents = ({
  dyDecisionId,
  dyVariationId,
  placement,
  name,
  position,
  visitorHash,
  label,
  mediaElement,
  constructorAdId,
}: UsePromoEventsParams) => {
  const onPromoView = useCallback(() => {
    dispatchTrackingEventGA4(
      getPromotionEventData({
        promoEvent: PromoEvent.PromoView,
        placement,
        name,
        position,
        dyDecisionId,
        dyVariationId,
        visitorHash,
        constructorAdId,
        label,
        type: mediaElement,
      })
    );
  }, [
    dyDecisionId,
    dyVariationId,
    placement,
    name,
    position,
    visitorHash,
    label,
    mediaElement,
    constructorAdId,
  ]);

  const onPromoClick = useCallback(
    ({ interaction, element }: PromoClickTrackingData = {}) => {
      dispatchTrackingEventGA4(
        getPromotionEventData({
          promoEvent: PromoEvent.PromoClick,
          placement,
          name,
          position,
          interaction,
          dyDecisionId,
          dyVariationId,
          visitorHash,
          constructorAdId,
          label,
          type: mediaElement,
          element,
        })
      );
    },
    [
      dyDecisionId,
      dyVariationId,
      placement,
      name,
      position,
      visitorHash,
      label,
      mediaElement,
      constructorAdId,
    ]
  );

  const onPromoClose = useCallback(() => {
    dispatchTrackingEventGA4(
      getPromotionEventData({
        promoEvent: PromoEvent.PromoClose,
        placement,
        name,
        position,
        dyDecisionId,
        dyVariationId,
        visitorHash,
        constructorAdId,
        label,
      })
    );
  }, [dyDecisionId, dyVariationId, placement, name, position, visitorHash, label, constructorAdId]);

  return {
    onPromoView,
    onPromoClick,
    onPromoClose,
  };
};
